import { MoreVertical, Trash2, Gift, Copy, ChevronUp, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub,
  DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MODES, MODE_HINT, eur, lineTotal, lineCost } from "@/lib/pricing";
import { NameAutocomplete } from "@/components/quote/NameAutocomplete";

const Num = ({ label, testId, ...p }) => (
  <div className="space-y-1">
    <div className="micro-label lg:hidden">{label}</div>
    <Input type="number" className="h-8 font-mono text-sm px-2" data-testid={testId} {...p} />
  </div>
);

const RowMenu = ({ line, sections, onChange, onRemove }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`line-menu-${line.id}`}><MoreVertical className="h-4 w-4" /></Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={() => onChange(line.id, { omaggio: !line.omaggio })} data-testid={`line-toggle-omaggio-${line.id}`}>
        <Gift className="h-4 w-4 mr-2" />{line.omaggio ? "Togli da omaggio" : "Segna come omaggio"}
      </DropdownMenuItem>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger><Copy className="h-4 w-4 mr-2" />Sposta in sezione</DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {sections.map((s) => <DropdownMenuItem key={s} onClick={() => onChange(line.id, { category: s })}>{s}</DropdownMenuItem>)}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSeparator />
      <DropdownMenuItem className="text-red-400" onClick={() => onRemove(line.id)} data-testid={`line-remove-${line.id}`}>
        <Trash2 className="h-4 w-4 mr-2" />Elimina riga
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const ModeSelect = ({ label, value, onChange, disabled, testId, cyan }) => (
  <div className="space-y-1">
    <div className="micro-label lg:hidden">{label}</div>
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={`h-8 text-xs font-mono px-2 ${cyan ? "text-cyan-200" : ""}`} title={MODE_HINT[value]} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {Object.entries(MODES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export const LINE_GRID = "lg:grid-cols-[56px_1fr_56px_80px_92px_76px_92px_88px_84px_56px]";

const MoveButtons = ({ line, onMove, first, last }) => (
  <div className="flex lg:flex-col">
    <Button size="icon" variant="ghost" className="h-9 w-9 lg:h-5 lg:w-6" disabled={first} onClick={() => onMove(line.id, -1)} title="Sposta su" aria-label="Sposta su" data-testid={`line-move-up-${line.id}`}><ChevronUp className="h-4 w-4" /></Button>
    <Button size="icon" variant="ghost" className="h-9 w-9 lg:h-5 lg:w-6" disabled={last} onClick={() => onMove(line.id, 1)} title="Sposta giù" aria-label="Sposta giù" data-testid={`line-move-down-${line.id}`}><ChevronDown className="h-4 w-4" /></Button>
  </div>
);

export const LineRow = ({ line, quoteDays, sections, catalog, onChange, onRemove, onMove, first, last }) => {
  const set = (patch) => onChange(line.id, patch);
  const fromItem = (it) => set({
    item_id: it.id, category: it.category, name: it.name, details: it.details || "", price: it.price, mode: it.mode,
    cost: it.cost || 0, cost_mode: it.cost_mode || it.mode, source: it.source || "proprio",
  });
  const noPrice = !line.omaggio && !+line.price;
  return (
    <div className={`grid grid-cols-2 ${LINE_GRID} gap-2 lg:gap-2.5 p-3 items-start transition-colors border-l-2 ${noPrice ? "border-red-500 bg-red-500/5" : "border-transparent hover:bg-slate-800/20"}`} data-testid={`line-row-${line.id}`} data-missing-price={noPrice || undefined}>
      <Num label="Qt" min={0} step="any" value={line.qty} onChange={(e) => set({ qty: e.target.value === "" ? "" : +e.target.value })} testId={`line-qty-${line.id}`} />
      <div className="col-span-2 lg:col-span-1 order-first lg:order-none space-y-1.5 min-w-0">
        <div className="flex items-center gap-2">
          <NameAutocomplete line={line} catalog={catalog} onText={(v) => set({ name: v })} onPick={fromItem} />
          {line.source === "noleggio" && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 whitespace-nowrap" data-testid={`line-rent-badge-${line.id}`}>Noleggio</span>}
        </div>
        <Textarea value={line.details} onChange={(e) => set({ details: e.target.value })} placeholder="Dettagli (composizione kit, note...)" rows={line.details ? Math.min(line.details.split("\n").length, 6) : 1} className="text-xs text-slate-400 min-h-[32px] py-1.5 resize-y" data-testid={`line-details-${line.id}`} />
      </div>
      <Num label="Giorni" min={1} value={line.days ?? ""} placeholder={String(quoteDays)} onChange={(e) => set({ days: e.target.value ? Math.max(1, parseInt(e.target.value, 10)) : null })} testId={`line-days-${line.id}`} />
      {line.omaggio ? (
        <Num label="Valore €" min={0} step="any" value={line.value} onChange={(e) => set({ value: +e.target.value || 0 })} testId={`line-value-${line.id}`} />
      ) : (
        <Num label="Prezzo/gg €" min={0} step="any" value={line.price} onChange={(e) => set({ price: e.target.value === "" ? "" : +e.target.value })} testId={`line-price-${line.id}`} className={`h-8 font-mono text-sm px-2 ${!+line.price ? "border-red-500 bg-red-500/10 text-red-300 ring-1 ring-red-500/40" : ""}`} title={!+line.price ? "Prezzo mancante" : undefined} />
      )}
      <ModeSelect label="Calcolo prezzo" value={line.mode} onChange={(v) => set({ mode: v })} disabled={line.omaggio} testId={`line-mode-${line.id}`} />
      <Num label="Costo €" min={0} step="any" value={line.cost ?? 0} onChange={(e) => set({ cost: e.target.value === "" ? "" : +e.target.value })} testId={`line-cost-${line.id}`} className="h-8 font-mono text-sm px-2 text-cyan-200" title="Costo interno (non compare nel PDF)" />
      <ModeSelect label="Calcolo costo" value={line.cost_mode || line.mode} onChange={(v) => set({ cost_mode: v })} testId={`line-cost-mode-${line.id}`} cyan />
      <div className="flex flex-col items-start lg:items-end justify-center min-h-8">
        <span className={`font-mono text-sm ${line.omaggio ? "text-emerald-400" : "text-slate-100"}`} data-testid={`line-total-${line.id}`}>
          {line.omaggio ? "Omaggio" : eur(lineTotal(line, quoteDays))}
        </span>
      </div>
      <div className="flex flex-col items-start lg:items-end justify-center min-h-8">
        <div className="micro-label lg:hidden">Costo</div>
        <span className={`font-mono text-sm ${+line.cost > 0 ? "text-cyan-300" : "text-slate-600"}`} data-testid={`line-cost-total-${line.id}`}>{eur(lineCost(line, quoteDays))}</span>
      </div>
      <div className="flex justify-end items-center gap-0.5"><MoveButtons line={line} onMove={onMove} first={first} last={last} /><RowMenu line={line} sections={sections} onChange={onChange} onRemove={onRemove} /></div>
    </div>
  );
};
